import { mergeWithDefault } from '@common/utils/mergeWithDefault';
import { AudioTrackLabel } from '@features/advancedVideoPlayer';
import { DIRECT_CLIP_CUSTOM_FIELD_KEYS } from '../constants';
import { Clip, DEFAULT_CLIP } from '../types';
import {
    BackendDirectClip,
    BackendMediaCustomFieldNames,
} from '../types/direct';
import {
    BackendMediaObject,
    BackendMediaObjectInstance,
    MediaIdentifier,
} from '../types/media';
import {
    mapDirectMediaIdentifiers,
    mapFrameRate,
    mapDirectMediaIdentifier,
} from './media.mappers';

const mapCustomFields = <TTarget, TNames extends string>(
    customFields: Record<TNames, { value?: unknown }>,
    map: Partial<Record<keyof TTarget, TNames>>,
): Partial<TTarget> => {
    return Object.entries(map).reduce(
        (acc, [targetKey, sourceKey]) => ({
            ...acc,
            [targetKey]: customFields[sourceKey as TNames]?.value ?? '',
        }),
        {} as Partial<TTarget>,
    );
};

export const mapDirectClipToFrontend = (raw: BackendDirectClip): Clip => {
    const metadata = raw.clipMetadata || {};
    const mediaObject = raw.mediaObject || ({} as BackendMediaObject);
    const instances = mediaObject.mediaObjectInstances || [];

    const mappedCustomFields = mapCustomFields<
        Clip,
        BackendMediaCustomFieldNames
    >(raw.customFields, DIRECT_CLIP_CUSTOM_FIELD_KEYS);

    const audioTracks: AudioTrackLabel[] = [];
    metadata.audioLayout?.groupOfSoundfieldGroups?.[0]?.soundFieldGroups?.forEach(
        (group) => {
            group.audioChannels?.forEach((ch) => {
                audioTracks.push({
                    label: ch.TagSymbol,
                    tagName: ch.TagName,
                    groupName: group.TagName,
                    flowNativeAudioChannel: ch.flowNativeAudioChannel,
                });
            });
        },
    );

    const hasProxy = instances.some((inst) => {
        if (inst.type !== 'PROXY') return false;

        if (inst.materialInfo) {
            return (
                inst.materialInfo.onFlow === true ||
                inst.materialInfo.online === true
            );
        }

        return true;
    });

    const mediaIdentifiers = mapDirectMediaIdentifiers(instances);

    return mergeWithDefault(
        {
            ...mappedCustomFields,
            id: raw.id,
            mediaObjectId: raw.mediaObjectId,
            clipName:
                mediaObject.realName ||
                mediaObject.name ||
                metadata.cardName ||
                '',
            audioMapping: metadata.audioLayout?.templateLabel || '',
            created: raw.created ? new Date(raw.created).toISOString() : '',
            mediaIdentifiers,
            hasProxy,
            audioTracks,
            frameRate: mapFrameRate(mediaObject.sampleRate),
            durationSeconds: mediaObject.durationSeconds,
            duration: mediaObject.duration,
        },
        DEFAULT_CLIP,
    );
};

export const mapDirectClipMediaToFrontend = (
    raw: BackendMediaObjectInstance,
): MediaIdentifier => {
    return mapDirectMediaIdentifier(raw);
};
